import { useEffect, useRef, useState } from "react"
import { Box, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { DateTime } from "luxon"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import {
    fetchMessages,
    FetchMessagesResponse,
    MerchantMessagesQuery,
} from "../../../../merchant/admin/messages/messagesApi"

const useStyles = makeStyles()((theme) => ({
    newsContainer: {
        color: theme.palette.common.white,
        background: theme.palette.tailwind.slate[900],
        padding: "0.5rem 1rem",
    },
    newsBox: {
        position: "relative",
        width: "100%",
        overflow: "hidden",
        minHeight: "1.75rem",
    },
    newsText: {
        whiteSpace: "nowrap",
        position: "absolute",
        willChange: "transform",
    },
}))

const selectMessages = (data: FetchMessagesResponse) => data?.messages || []

export function NewsTicker() {
    const { classes } = useStyles()
    const { merchantId } = useMerchant()
    const [currentMessageIndex, setCurrentMessageIndex] = useState(0)
    const textRef = useRef<HTMLDivElement>(null)

    const todayDate = DateTime.now()
    const { data: messagesData = [] } = useApiQuery(fetchMessages, {
        queryName: MerchantMessagesQuery.LIST,
        dependencies: {
            merchantId,
            startDate: todayDate.toISODate(),
            endDate: todayDate.toISODate(),
        },
        select: selectMessages,
    })

    useEffect(() => {
        const textElement = textRef.current
        if (textElement) {
            const containerWidth = textElement.parentElement!.offsetWidth
            const textWidth = textElement.offsetWidth

            textElement.style.transform = `translateX(${containerWidth}px)`

            // Text movement speed - pixels per second
            const pixelsPerSecond = 100
            const duration = ((textWidth + containerWidth) / pixelsPerSecond) * 1000

            const keyframes = [
                { transform: `translateX(${containerWidth}px)` },
                { transform: `translateX(-${textWidth}px)` },
            ]

            const animation = textElement.animate(keyframes, {
                duration: duration,
                easing: "linear",
                iterations: Infinity, // Ensure infinite iterations for the single message
            })

            const timeoutId = setTimeout(() => {
                if (messagesData.length > 1) {
                    setCurrentMessageIndex((prevIndex) => (prevIndex + 1) % messagesData.length)
                } else {
                    // Reset the animation for the single message
                    animation.cancel()
                    textElement.style.transform = `translateX(${containerWidth}px)`
                    animation.play() // Restart the animation
                }
            }, duration)

            return () => {
                animation.cancel()
                clearTimeout(timeoutId)
            }
        }
        return undefined
    }, [currentMessageIndex, messagesData])

    return (
        <Box className={classes.newsContainer}>
            <Box className={classes.newsBox}>
                <div ref={textRef} className={classes.newsText}>
                    {!!messagesData.length && (
                        <Typography variant="subtitle1">{messagesData[currentMessageIndex]?.body}</Typography>
                    )}
                </div>
            </Box>
        </Box>
    )
}
